// Privacy Policy Page
import { useEffect } from "react";
import { Link } from "react-router-dom";
import Navigation from "../components/shared/Navigation";
import Footer from "../components/shared/Footer";
import { usePageMeta } from "../components/shared/SEOHelper";

const PrivacyPolicyPage = () => {
  usePageMeta({
    title: "Privacy Policy | Annelie Lily Yoga | Frome, Somerset",
    description: "Privacy policy for Annelie Lily Yoga (Seventh Sense Healing). Learn how we collect, use, and protect your personal data when booking yoga classes and",
    canonical: "https://annelielily.com/privacy-policy"
  });

  return (
    <div className="min-h-screen bg-cream">
      <Navigation />
      
      <main className="pt-28 pb-16 px-6 md:px-12">
        <div className="max-w-3xl mx-auto">
          <h1 className="font-heading text-3xl md:text-4xl text-neutral-800 mb-8">
            Privacy Policy
          </h1>
          
          <div className="prose prose-neutral max-w-none space-y-6 text-neutral-700">
            <p className="font-semibold">
              Welcome to Seventh Sense Healing Limited's Privacy Notice.
            </p>
            <p>
              Seventh Sense Healing Limited respects your privacy and is committed to protecting your personal data. 
              This privacy notice will inform you as to how we look after your personal data when you visit our website 
              (regardless of where you visit it from) and tell you about your privacy rights and how the law protects you.
            </p>

            <section>
              <h2 className="font-heading text-xl text-neutral-800 mt-8 mb-4">1. Important Information and Who We Are</h2>
              
              <h3 className="font-semibold mt-6 mb-2">Purpose of this privacy notice</h3>
              <p>
                This privacy notice aims to give you information on how Seventh Sense Healing Limited collects and processes 
                your personal data through your use of this website, including any data you may provide through this website 
                when you complete a health form, book a yoga class, book a massage, reiki or other treatment, sign up to our 
                newsletter, purchase a product or service or take part in a competition or offer.
              </p>

              <h3 className="font-semibold mt-6 mb-2">Controller</h3>
              <p>
                Seventh Sense Healing Limited is the controller and responsible for your personal data (collectively referred 
                to as "Seventh Sense Healing", "we", "us" or "our" in this privacy notice).
              </p>
              <p>
                We have appointed Annelie Carver as a Data Privacy Manager who is responsible for overseeing questions in 
                relation to this Privacy Notice. If you have any questions about this Privacy Notice, including any requests 
                to exercise your legal rights, please contact Annelie Carver using the details set out below.
              </p>

              <h3 className="font-semibold mt-6 mb-2">Contact details</h3>
              <p>Our full details are: Seventh Sense Healing Limited</p>
              <ul className="list-none space-y-1">
                <li><strong>Data Privacy Manager:</strong> Annelie Carver</li>
                <li><strong>Email:</strong> <a href="mailto:seventhsensehealing@outlook.com" className="text-gold hover:underline">seventhsensehealing@outlook.com</a></li>
                <li><strong>Address:</strong> C/O Prydis, Senate Court, Southernhay Gardens, Exeter, England, EX1 1NT</li>
                <li><strong>Telephone:</strong> <a href="tel:07540995021" className="text-gold hover:underline">07540 995021</a></li>
              </ul>
              <p className="mt-4">
                You have the right to make a complaint at any time to the Information Commissioner's Office (ICO), the UK 
                supervisory authority for data protection issues (www.ico.org.uk). We would, however, appreciate the chance 
                to deal with your concerns before you approach the ICO so please contact us in the first instance.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-xl text-neutral-800 mt-8 mb-4">2. The Data We Collect About You</h2>
              <p>
                Personal data, or personal information, means any information about an individual from which that person can 
                be identified. It does not include data where the identity has been removed (anonymous data).
              </p>
              <p>We may collect, use, store and transfer different kinds of personal data about you which we have grouped together as follows:</p>
              <ul className="list-disc pl-6 space-y-2 mt-4">
                <li><strong>Identity Data:</strong> includes first name, last name, username or similar identifier, title, date of birth and gender.</li>
                <li><strong>Contact Data:</strong> includes billing address, delivery address, email address and telephone numbers.</li>
                <li><strong>Transaction Data:</strong> includes details about payments to and from you and other details of products and services you have purchased from us.</li>
                <li><strong>Technical Data:</strong> includes internet protocol (IP) address, your login data, browser type and version, time zone setting and location.</li>
                <li><strong>Profile Data:</strong> includes your username and password, purchases or orders made by you, your interests, preferences, feedback and survey responses.</li>
                <li><strong>Usage Data:</strong> includes information about how you use our website, products and services.</li>
                <li><strong>Marketing and Communications Data:</strong> includes your preferences in receiving marketing from us and your communication preferences.</li>
              </ul>
              <p className="mt-4">
                We do collect health information that you provide when you complete a health form either online via the website 
                or in class. This information is necessary to ensure we can provide you with safe and appropriate yoga classes 
                and treatments.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-xl text-neutral-800 mt-8 mb-4">3. How Is Your Personal Data Collected?</h2>
              <p>We use different methods to collect data from and about you including through:</p>
              <ul className="list-disc pl-6 space-y-2 mt-4">
                <li><strong>Direct interactions:</strong> You may give us your Identity, Contact and Financial Data by filling in forms or by corresponding with us by post, phone, email or otherwise.</li>
                <li><strong>Third parties:</strong> Contact, Financial and Transaction Data from providers of technical, payment and delivery services such as our booking and payment service providers.</li>
              </ul>
            </section>

            <section>
              <h2 className="font-heading text-xl text-neutral-800 mt-8 mb-4">4. How We Use Your Personal Data</h2>
              <p>We will only use your personal data when the law allows us to. Most commonly, we will use your personal data in the following circumstances:</p>
              <ul className="list-disc pl-6 space-y-2 mt-4">
                <li>Where we need to perform the contract we are about to enter into or have entered into with you.</li>
                <li>Where it is necessary for our legitimate interests (or those of a third party) and your interests and fundamental rights do not override those interests.</li>
                <li>Where we need to comply with a legal or regulatory obligation.</li>
              </ul>
              <p className="mt-4">
                Generally we do not rely on consent as a legal basis for processing your personal data other than in relation 
                to sending third party direct marketing communications to you via email or text message. You have the right to 
                withdraw consent to marketing at any time by contacting us.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-xl text-neutral-800 mt-8 mb-4">5. Marketing</h2>
              <p>
                You will receive marketing communications from us if you have requested information from us or purchased goods 
                or services from us and, in each case, you have not opted out of receiving that marketing.
              </p>
              <p className="mt-4">
                <strong>Opting out:</strong> You can ask us to stop sending you marketing messages at any time by following 
                the opt-out links on any marketing message sent to you or by contacting us at any time.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-xl text-neutral-800 mt-8 mb-4">6. Cookies</h2>
              <p>
                You can set your browser to refuse all or some browser cookies, or to alert you when websites set or access 
                cookies. If you disable or refuse cookies, please note that some parts of this website may become inaccessible 
                or not function properly. For more information about the cookies we use, please see our{" "}
                <Link to="/cookie-policy" className="text-gold hover:underline">Cookie Policy</Link>.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-xl text-neutral-800 mt-8 mb-4">7. Data Security</h2>
              <p>
                We have put in place appropriate security measures to prevent your personal data from being accidentally lost, 
                used or accessed in an unauthorised way, altered or disclosed. In addition, we limit access to your personal 
                data to those employees, agents, contractors and other third parties who have a business need to know.
              </p>
              <p className="mt-4">
                We have put in place procedures to deal with any suspected personal data breach and will notify you and any 
                applicable regulator of a breach where we are legally required to do so.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-xl text-neutral-800 mt-8 mb-4">8. Data Retention</h2>
              <p>
                We will only retain your personal data for as long as necessary to fulfil the purposes we collected it for, 
                including for the purposes of satisfying any legal, accounting, or reporting requirements.
              </p>
              <p className="mt-4">
                By law we have to keep basic information about our customers (including Contact, Identity, Financial and 
                Transaction Data) for six years after they cease being customers for tax purposes.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-xl text-neutral-800 mt-8 mb-4">9. Your Legal Rights</h2>
              <p>Under certain circumstances, you have rights under data protection laws in relation to your personal data. You have the right to:</p>
              <ul className="list-disc pl-6 space-y-2 mt-4">
                <li><strong>Request access</strong> to your personal data (commonly known as a "data subject access request").</li>
                <li><strong>Request correction</strong> of the personal data that we hold about you.</li>
                <li><strong>Request erasure</strong> of your personal data.</li>
                <li><strong>Object to processing</strong> of your personal data where we are relying on a legitimate interest.</li>
                <li><strong>Request restriction of processing</strong> of your personal data.</li>
                <li><strong>Request the transfer</strong> of your personal data to you or to a third party.</li>
                <li><strong>Withdraw consent at any time</strong> where we are relying on consent to process your personal data.</li>
              </ul>
              <p className="mt-4">
                If you wish to exercise any of the rights set out above, please contact us at{" "}
                <a href="mailto:seventhsensehealing@outlook.com" className="text-gold hover:underline">seventhsensehealing@outlook.com</a>.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-xl text-neutral-800 mt-8 mb-4">10. Third Parties</h2>
              <p>We may share your personal data with:</p>
              <ul className="list-disc pl-6 space-y-2 mt-4">
                <li>Service providers who provide IT and administration services.</li>
                <li>Professional advisers including lawyers, bankers, auditors and insurers.</li>
                <li>HM Revenue & Customs, regulators and other authorities.</li>
                <li>Our booking system provider.</li>
              </ul>
              <p className="mt-4">
                We do not transfer your personal data outside the European Economic Area (EEA).
              </p>
            </section>

            <div className="mt-12 pt-8 border-t border-gold/20">
              <p className="text-sm text-neutral-500">
                Last updated: December 2025
              </p>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PrivacyPolicyPage;
